import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface Property {
  id: string;
  tenantId: string;
  projectId?: string | null;
  type: 'flat' | 'house' | 'land';
  address: string;
  addressFull?: string | null;
  lat: number;
  lng: number;
  street?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  district?: string | null;
  region?: string | null;
  postalCode?: string | null;
  floorArea?: number | null;
  lotArea?: number | null;
  localType?: string | null;
  ownership?: string | null;
  rating?: string | null;
  houseType?: string | null;
  landType?: string | null;
  panoramaUrl?: string | null;
  createdAt: string;
  project?: {
    id: string;
    name: string;
    color?: string;
  } | null;
  latestValuation?: {
    id: string;
    status: string;
    avgPrice?: number | null;
    minPrice?: number | null;
    maxPrice?: number | null;
    avgPriceM2?: number | null;
    calcArea?: number | null;
    createdAt: string;
    leadId?: string | null;
    contactId?: string | null;
  } | null;
}

export interface PropertyMapItem {
  id: string;
  type: 'flat' | 'house' | 'land';
  address: string;
  addressFull?: string | null;
  lat: number;
  lng: number;
  city?: string | null;
  floorArea?: number | null;
  lotArea?: number | null;
  localType?: string | null;
  panoramaUrl?: string | null;
  project?: {
    id: string;
    name: string;
    color?: string;
  } | null;
  avgPrice?: number | null;
  avgPriceM2?: number | null;
  valuationStatus?: string | null;
  leadId?: string | null;
}

interface PropertiesResponse {
  data: Property[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface PropertiesFilters {
  projectId?: string;
  type?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useProperties(filters: PropertiesFilters = {}) {
  const { currentTenant } = useTenant();

  const params = new URLSearchParams();
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.type) params.set('type', filters.type);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const queryString = params.toString();
  const path = `/properties${queryString ? `?${queryString}` : ''}`;

  const query = useQuery({
    queryKey: ['properties', filters, currentTenant?.id],
    queryFn: () => api.get<PropertiesResponse>(path),
    enabled: !!currentTenant,
  });

  return {
    ...query,
    data: query.data?.data,
    meta: query.data?.meta,
  };
}

export function usePropertiesMap(projectId?: string) {
  const { currentTenant } = useTenant();

  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);

  const queryString = params.toString();
  const path = `/properties/map${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['properties-map', projectId, currentTenant?.id],
    queryFn: () => api.get<PropertyMapItem[]>(path),
    enabled: !!currentTenant,
  });
}

export function useProperty(id: string) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['property', id, currentTenant?.id],
    queryFn: () => api.get<Property>(`/properties/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  flat: 'Byt',
  house: 'Dům',
  land: 'Pozemek',
};

export const RATING_LABELS: Record<string, string> = {
  bad: 'Špatný',
  nothing_much: 'Nic moc',
  good: 'Dobrý',
  very_good: 'Velmi dobrý',
  new: 'Novostavba',
  excellent: 'Excelentní',
};

export const OWNERSHIP_LABELS: Record<string, string> = {
  private: 'Osobní',
  cooperative: 'Družstevní',
  council: 'Obecní',
  other: 'Jiné',
};
